import { FC } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useRouterPush } from '@/client/hooks'

type SEO = {
  title?: string
  description?: string
  copyright?: string
  abstract?: string
  source_url?: string
  domain?: string
  og_locale?: string
  robots?: {
    index?: string
    follow?: string
    'max-snippet'?: string
    'max-image-preview'?: string
    'max-video-preview'?: string
  }
  canonical?: string
  alternateTags: []
  type?: string
  image?: string
}

interface HeadersProps {
  seo?: SEO
  noIndex?: boolean
}

const Headers: FC<HeadersProps> = ({ seo, noIndex = false }) => {
  const router = useRouter()
  const { pathname, locale } = useRouterPush()

  const generateCanonical = () => {
    const siteUrl = seo?.domain || (typeof window !== 'undefined' ? window.location.origin : '')

    if (!siteUrl) return ''
    return `${siteUrl}${router.asPath.split('?')[0]}`
  }

  const cleanUrl = (url: string) => {
    try {
      const parsedUrl = new URL(url)
      return parsedUrl.pathname !== '//' ? parsedUrl.origin + parsedUrl.pathname : parsedUrl.origin + '/'
    } catch {
      return url.split('?')[0]
    }
  }

  const normalizeHreflang = (lang: string) => {
    const map: Record<string, string> = {
      es: 'es-ES',
      it: 'it-IT',
      nl: 'nl-NL',
      au: 'en-AU',
      fr: 'fr-FR',
      de: 'de',
      us: 'en-US',
      uk: 'en-GB'
    }

    return map[lang] ?? lang
  }

  const homepageAlternates = {
    de: 'https://etikett.de/',
    'en-GB': 'https://label.co.uk/',
    'fr-FR': 'https://labelletiquette.fr/',
    'it-IT': 'https://etichetta.it/',
    'nl-NL': 'https://unieketiket.nl/',
    'es-ES': 'https://etiqueta.es/',
    'en-AU': 'https://primelabels.com.au/',
    'en-US': 'https://labelmakers.com/'
  }

  return (
    <Head>
      <meta name='viewport' content='initial-scale=1.0, width=device-width' />
      <title>{seo?.title || 'Etikett'}</title>
      <meta name='description' content={seo?.description || 'Etikett description'} />
      <meta
        name='robots'
        content={
          `${noIndex ? 'noindex, nofollow' : 'index, follow'}, ${seo?.robots?.['max-snippet']}, ${
            seo?.robots?.['max-image-preview']
          }, ${seo?.robots?.['max-video-preview']}` || 'index, follow'
        }
      />
      <meta name='copyright' content={seo?.copyright || 'etikett.de'} />
      <meta name='abstract' content={seo?.abstract || seo?.description || 'Etikett description'} />$
      {seo && <meta property='og:url' content={`${seo['domain']}${router.asPath}` || 'http://etikett.de'} />}
      <meta property='og:type' content={seo?.type || 'website'} />
      <meta
        property='og:locale'
        content={seo?.domain === 'https://label.co.uk' ? 'en_GB' : seo?.og_locale || 'en_EN'}
      />
      <meta property='og:title' content={seo?.title} />
      <meta property='og:description' content={seo?.description} />
      <meta property='og:image' content={seo?.source_url || 'http://etikett.de/logo.png'} />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      <meta property='fb:admins' content='627008' />
      <meta property='fb:app_id' content='312936488776247' />
      {/* Canonical Tag  */}
      <link rel='canonical' href={generateCanonical()} />
      {/* Alternate Tag  */}
      {seo &&
        seo?.alternateTags?.length > 0 &&
        seo.alternateTags.map(({ hreflang, href }) => (
          <link key={hreflang} rel='alternate' hrefLang={hreflang} href={href} />
        ))}
      {/* {pathname === '/' &&
        Object.entries(homepageAlternates).map(([hrefLang, href]) => (
          <link key={hrefLang} rel='alternate' hrefLang={hrefLang} href={href} />
        ))} */}
    </Head>
  )
}

export default Headers
